import { forwardRef, useEffect, useMemo, useState } from 'react';
import { HiArrowLeft } from 'react-icons/hi';
import { FaSave } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import _isEmpty from 'lodash/isEmpty';

import { Modal, ModalMethods, Text, Loader, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';

type PricingRule = {
  id: string;
  operator: '<' | '<=' | '>' | '>=';
  price: string;
  gst: string;
};

type ExtraChargeRow = {
  id: string;
  label: string;
  amount: string;
  isDefault: boolean;
};

type DefaultPriceItem = {
  name: string;
  value: number;
  is_default: boolean;
};

/** Backend enum: NOTAX, FLAT, ADVANCE */
export type TaxType = 'NOTAX' | 'FLAT' | 'ADVANCE';

type PricingDataResponse = {
  id: number;
  custom_outfit_id: number;
  default_price: DefaultPriceItem[];
  pricing_rules?: Array<{
    id: number;
    operator: '<' | '<=' | '>' | '>=';
    price: string;
    gst: number;
  }>;
  advance_tax_name?: string;
  flat_gst?: number;
  flat_tax_name?: string;
  no_tax?: boolean;
  is_flat_gst_default?: boolean;
  tax_type?: TaxType | null;
};

type PricingModalProps = {
  customOutfitId: number;
  initialFlatGst?: number;
  initialTaxName?: string;
  onSaved?: () => void;
};

const uid = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const sanitizeNumber = (value: string): number => {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const num = Number(cleaned);

  if (Number.isFinite(num)) {
    return num;
  }

  return 0;
};

const PricingModal = forwardRef<ModalMethods, PricingModalProps>(
  ({ customOutfitId, initialFlatGst, initialTaxName, onSaved }, ref) => {
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [extraCharges, setExtraCharges] = useState<ExtraChargeRow[]>([]);

    /** UI state: which tax tab is currently shown (None / Flat / Advanced) */
    const [taxMode, setTaxMode] = useState<'none' | 'flat' | 'advanced'>('none');
    const [flatTaxName, setFlatTaxName] = useState(initialTaxName || 'GST');
    const [advanceTaxName, setAdvanceTaxName] = useState(initialTaxName || 'GST');
    const [flatGst, setFlatGst] = useState(String(initialFlatGst ?? 0));
    /** Which tax section is "Mark as default" - derived from tax_type only */
    /** Value for tax_type key when saving (which section is marked as default) */
    const [defaultTaxSection, setDefaultTaxSection] = useState<'none' | 'flat' | 'advanced' | null>(
      null
    );
    const [pricingRules, setPricingRules] = useState<PricingRule[]>([
      { id: uid(), operator: '<', price: '1500', gst: '5' },
    ]);

    useEffect(() => {
      const fetchPricingData = async () => {
        if (!customOutfitId) {
          return;
        }

        setIsLoading(true);

        try {
          const response = await api.getRequest(`pricing/${customOutfitId}`);

          if (response.status && response.data) {
            const data: PricingDataResponse = response.data;

            // Set default price data (default_price is array of { name, value, is_default })
            if (data.default_price && Array.isArray(data.default_price)) {
              const chargesList: ExtraChargeRow[] = data.default_price.map((item) => ({
                id: uid(),
                label: item.name ?? '',
                amount: String(item.value ?? 0),
                isDefault: item.is_default ?? true,
              }));
              setExtraCharges(chargesList);
            }

            // Initialize flatGst from API if available (for mode switching)
            if (data.flat_gst !== undefined && data.flat_gst !== null) {
              setFlatGst(String(data.flat_gst));
            }

            // Set defaultTaxSection from tax_type only (NOTAX→none, FLAT→flat, ADVANCE→advanced)
            if (
              data.tax_type === 'NOTAX' ||
              data.tax_type === 'FLAT' ||
              data.tax_type === 'ADVANCE'
            ) {
              setDefaultTaxSection(
                data.tax_type === 'NOTAX' ? 'none' : data.tax_type === 'FLAT' ? 'flat' : 'advanced'
              );
            } else {
              setDefaultTaxSection(null);
            }

            // Determine tax mode from tax_type first (API enum: NOTAX, FLAT, ADVANCE)
            if (
              data.tax_type === 'NOTAX' ||
              data.tax_type === 'FLAT' ||
              data.tax_type === 'ADVANCE'
            ) {
              setTaxMode(
                data.tax_type === 'NOTAX' ? 'none' : data.tax_type === 'FLAT' ? 'flat' : 'advanced'
              );
            } else {
              // Fallback when tax_type is null: pricing_rules (Advanced) > flat_gst (Flat) > no_tax
              if (data.pricing_rules && data.pricing_rules.length > 0) {
                setTaxMode('advanced');
                setDefaultTaxSection('advanced');
                setAdvanceTaxName(data.advance_tax_name || 'GST');
                setPricingRules(
                  data.pricing_rules.map((rule) => ({
                    id: uid(),
                    operator: rule.operator,
                    price: rule.price,
                    gst: String(rule.gst),
                  }))
                );
              } else if (
                data.flat_gst !== undefined &&
                data.flat_gst !== null &&
                data.flat_gst > 0
              ) {
                setTaxMode('flat');
                setDefaultTaxSection('flat');
                setFlatTaxName(data.flat_tax_name || 'GST');
              } else if (data.no_tax) {
                setTaxMode('none');
                setDefaultTaxSection('none');
              } else {
                setTaxMode('none');
                setDefaultTaxSection('none');
              }
            }

            // Always load pricing_rules from API when present (regardless of tax_type)
            // so they are available when user switches to Advanced mode
            if (data.pricing_rules && data.pricing_rules.length > 0) {
              setAdvanceTaxName(data.advance_tax_name || 'GST');
              setPricingRules(
                data.pricing_rules.map((rule) => ({
                  id: uid(),
                  operator: rule.operator,
                  price: rule.price,
                  gst: String(rule.gst),
                }))
              );
            }

            // Always load flat and advance tax names from API to preserve when switching modes
            if (data.flat_tax_name !== undefined && data.flat_tax_name !== null) {
              setFlatTaxName(data.flat_tax_name || 'GST');
            }

            if (data.advance_tax_name !== undefined && data.advance_tax_name !== null) {
              setAdvanceTaxName(data.advance_tax_name || 'GST');
            }
          }
        } catch (err) {
          if (err instanceof Error) {
            toasts('error', err.message, 'pricing-fetch-error');
          }

          // If API fails, reset to empty/default values
          setExtraCharges([]);
          setFlatTaxName('GST');
          setAdvanceTaxName('GST');
          setFlatGst('0');
          setTaxMode('none');
          setDefaultTaxSection(null);
        } finally {
          setIsLoading(false);
        }
      };

      fetchPricingData();
    }, [customOutfitId, initialFlatGst, initialTaxName]);

    const canSave = useMemo(() => {
      if (isLoading || isSaving) {
        return false;
      }

      const hasInvalidDefaultCharge = extraCharges.some((row) => {
        if (!row.isDefault) {
          return false;
        }

        return _isEmpty(row.label.trim()) || _isEmpty(row.amount.trim());
      });

      return !hasInvalidDefaultCharge;
    }, [extraCharges, isSaving, isLoading]);

    const buildDefaultPricePayload = (): DefaultPriceItem[] => {
      return extraCharges
        .filter((row) => !_isEmpty(row.label.trim()))
        .map((row) => ({
          name: row.label.trim(),
          value: sanitizeNumber(row.amount),
          is_default: row.isDefault,
        }));
    };

    const handleSave = async () => {
      if (!customOutfitId) {
        toasts('error', 'Item id missing', 'pricing-id-missing');

        return;
      }

      if (!canSave) {
        toasts('error', 'Please fill required fields', 'pricing-invalid');

        return;
      }

      const taxTypeValue: TaxType | null =
        (defaultTaxSection ?? taxMode) === 'none'
          ? 'NOTAX'
          : (defaultTaxSection ?? taxMode) === 'flat'
            ? 'FLAT'
            : 'ADVANCE';

      const payload: Record<string, unknown> = {
        custom_outfit_id: customOutfitId,
        default_price: buildDefaultPricePayload(),
        tax_type: taxTypeValue,
      };

      // Always include flat_gst to preserve flat values when saving in any mode
      payload.flat_gst = {
        gst: sanitizeNumber(flatGst),
        name: flatTaxName.trim() || 'GST',
      };

      // Always include advance_tax_name and pricing_rules to preserve advanced values
      payload.advance_tax_name = advanceTaxName.trim() || 'GST';
      payload.pricing_rules = pricingRules
        .filter((r) => !_isEmpty(r.price.trim()) && !_isEmpty(r.gst.trim()))
        .map((r) => ({
          operator: r.operator,
          price: r.price.trim(),
          gst: sanitizeNumber(r.gst),
        }));

      setIsSaving(true);

      try {
        const response = await api.putRequest('pricing/update', payload);

        if (response.status) {
          toasts('success', 'Pricing saved', 'pricing-saved');

          if (onSaved) {
            onSaved();
          }

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }
        } else {
          toasts('error', response.message || 'Failed to save pricing', 'pricing-save-failed');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'pricing-save-failed');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const headerComponent = (
      <div className="flex items-center justify-between w-full pr-8">
        <button
          type="button"
          onClick={() => {
            if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
              ref.current.hide();
            }
          }}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <HiArrowLeft size={18} />
          <Text size="small" color="black" className="font-inter">
            Back
          </Text>
        </button>
        <div className="flex items-center gap-2">
          <Text size="large" fontWeight={700} className="font-inter">
            Enter price
          </Text>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="px-4 py-2 bg-[#4D7AFF] text-white rounded-lg font-semibold transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSaving ? (
            <>
              <Loader />
              <Text size="small" color="white" className="font-inter font-medium">
                Saving...
              </Text>
            </>
          ) : (
            <>
              <FaSave size={16} />
              <Text size="small" color="white" className="font-inter font-medium">
                Save
              </Text>
            </>
          )}
        </button>
      </div>
    );

    return (
      <Modal
        ref={ref}
        size="xl"
        headerComponent={headerComponent}
        showFooter={false}
        showCloseIcon={false}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full min-h-[520px]">
            <Loader />
            <Text className="font-inter text-sm ml-3" color="black">
              Loading pricing data...
            </Text>
          </div>
        ) : (
          <div className="flex flex-col h-full min-h-[520px]">
            <Text className="font-inter text-sm text-gray-600 mt-1 block text-center" color="black">
              Set the selling price, discount, and tax for this item. All values will be applied
              automatically during billing
            </Text>

            <div className="flex items-center justify-center gap-2 mt-4">
              <Text className="font-inter text-sm text-gray-700" color="black">
                Check the box
              </Text>
              <input
                type="checkbox"
                checked={true}
                disabled
                className="h-4 w-4 accent-black cursor-default"
              />
              <Text className="font-inter text-sm text-gray-700" color="black">
                to make it a default price
              </Text>
            </div>

            <div className="mt-5 border border-[#E5E7EB] rounded-2xl p-5 bg-white">
              <Text className="font-inter font-semibold text-base mb-4" color="black">
                Item level pricing
              </Text>

              <div className="space-y-3">
                {extraCharges.map((row, idx) => (
                  <div key={row.id} className="flex items-center gap-3">
                    <div className="w-6 text-sm text-gray-700 font-inter">{idx + 1}.</div>
                    <input
                      type="text"
                      value={row.label}
                      onChange={(e) =>
                        setExtraCharges((prev) =>
                          prev.map((p) => (p.id === row.id ? { ...p, label: e.target.value } : p))
                        )
                      }
                      placeholder="Charge name"
                      className="flex-1 h-12 px-4 border border-gray-200 rounded-xl text-sm font-inter bg-white"
                    />
                    <div className="w-[120px]">
                      <div className="flex items-center gap-2 h-12 px-4 border border-gray-200 rounded-xl bg-white">
                        <span className="text-gray-600 font-inter text-sm">₹</span>
                        <input
                          type="text"
                          value={row.amount}
                          onChange={(e) =>
                            setExtraCharges((prev) =>
                              prev.map((p) =>
                                p.id === row.id ? { ...p, amount: e.target.value } : p
                              )
                            )
                          }
                          className="w-full outline-none font-inter text-sm"
                          inputMode="decimal"
                        />
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={row.isDefault}
                      onChange={(e) =>
                        setExtraCharges((prev) =>
                          prev.map((p) =>
                            p.id === row.id ? { ...p, isDefault: e.target.checked } : p
                          )
                        )
                      }
                      className="h-5 w-5 accent-[#4D7AFF]"
                      title="Include in default price"
                    />
                    <button
                      type="button"
                      onClick={() => setExtraCharges((prev) => prev.filter((p) => p.id !== row.id))}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      aria-label="Delete charge"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() =>
                    setExtraCharges((prev) => [
                      ...prev,
                      { id: uid(), label: '', amount: '', isDefault: true },
                    ])
                  }
                  className="mt-2 px-4 py-2 border border-[#2563EB] text-[#2563EB] rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-[#EFF6FF] transition-colors w-fit"
                >
                  <span className="font-bold text-lg leading-none">+</span>
                  <span>Add Extra Charge</span>
                </button>
              </div>
            </div>

            <div className="mt-5 border border-[#E5E7EB] rounded-2xl p-5 bg-white">
              <Text className="font-inter font-semibold text-base mb-3" color="black">
                Tax rules (item level)
              </Text>

              <div className="inline-flex items-center bg-[#E5E7EB] rounded-full p-1 gap-1">
                <button
                  type="button"
                  onClick={() => setTaxMode('none')}
                  className={`px-4 py-1 rounded-full text-sm font-inter ${
                    taxMode === 'none' ? 'bg-black text-white' : 'text-black'
                  }`}
                >
                  No Tax
                </button>
                <button
                  type="button"
                  onClick={() => setTaxMode('flat')}
                  className={`px-4 py-1 rounded-full text-sm font-inter ${
                    taxMode === 'flat' ? 'bg-black text-white' : 'text-black'
                  }`}
                >
                  Flat
                </button>
                <button
                  type="button"
                  onClick={() => setTaxMode('advanced')}
                  className={`px-4 py-1 rounded-full text-sm font-inter ${
                    taxMode === 'advanced' ? 'bg-black text-white' : 'text-black'
                  }`}
                >
                  Advanced
                </button>
              </div>

              {taxMode === 'none' && (
                <div className="mt-4 flex items-center gap-5">
                  <Text className="font-inter text-sm" color="black">
                    Mark as default
                  </Text>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={defaultTaxSection === 'none'}
                    onClick={() =>
                      setDefaultTaxSection((prev) => (prev === 'none' ? null : 'none'))
                    }
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#4D7AFF] focus:ring-offset-2 ${
                      defaultTaxSection === 'none' ? 'bg-[#4D7AFF]' : 'bg-[#E5E7EB]'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                        defaultTaxSection === 'none' ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              )}

              {taxMode === 'flat' && (
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Text className="font-inter text-sm mb-2" color="black">
                        Tax Name
                      </Text>
                      <input
                        type="text"
                        value={flatTaxName}
                        onChange={(e) => setFlatTaxName(e.target.value)}
                        className="w-full h-11 px-4 border border-gray-200 rounded-xl text-sm font-inter outline-none"
                      />
                    </div>
                    <div>
                      <Text className="font-inter text-sm mb-2" color="black">
                        Tax Percentage (%)
                      </Text>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={flatGst}
                          onChange={(e) => setFlatGst(e.target.value)}
                          className="w-full h-11 px-4 border border-gray-200 rounded-xl text-sm font-inter outline-none"
                          inputMode="decimal"
                        />
                        <Text className="font-inter text-sm" color="black">
                          %
                        </Text>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <Text className="font-inter text-sm" color="black">
                      Mark as default
                    </Text>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={defaultTaxSection === 'flat'}
                      onClick={() =>
                        setDefaultTaxSection((prev) => (prev === 'flat' ? null : 'flat'))
                      }
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#4D7AFF] focus:ring-offset-2 ${
                        defaultTaxSection === 'flat' ? 'bg-[#4D7AFF]' : 'bg-[#E5E7EB]'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                          defaultTaxSection === 'flat' ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}

              {taxMode === 'advanced' && (
                <div className="mt-4">
                  <div className="mb-4">
                    <Text className="font-inter text-sm mb-2" color="black">
                      Tax Name
                    </Text>
                    <input
                      type="text"
                      value={advanceTaxName}
                      onChange={(e) => setAdvanceTaxName(e.target.value)}
                      className="w-full h-11 px-4 border border-gray-200 rounded-xl text-sm font-inter outline-none"
                    />
                  </div>

                  <div className="space-y-3">
                    {pricingRules.map((rule) => (
                      <div
                        key={rule.id}
                        className="flex items-center gap-3 bg-[#E5E7EB] rounded-full px-3 py-2"
                      >
                        <Text className="font-inter text-sm" color="black">
                          Price
                        </Text>
                        <select
                          value={rule.operator}
                          onChange={(e) =>
                            setPricingRules((prev) =>
                              prev.map((p) =>
                                p.id === rule.id
                                  ? { ...p, operator: e.target.value as PricingRule['operator'] }
                                  : p
                              )
                            )
                          }
                          className="h-9 px-3 rounded-full border border-gray-200 bg-white text-sm font-inter outline-none"
                        >
                          <option value="<">&lt;</option>
                          <option value="<=">&lt;=</option>
                          <option value=">">&gt;</option>
                          <option value=">=">&gt;=</option>
                        </select>
                        <div className="flex items-center gap-2 h-9 px-3 rounded-full border border-gray-200 bg-white">
                          <span className="text-gray-600 font-inter text-sm">₹</span>
                          <input
                            type="text"
                            value={rule.price}
                            onChange={(e) =>
                              setPricingRules((prev) =>
                                prev.map((p) =>
                                  p.id === rule.id ? { ...p, price: e.target.value } : p
                                )
                              )
                            }
                            className="w-24 outline-none font-inter text-sm bg-transparent"
                            inputMode="decimal"
                          />
                        </div>
                        <Text className="font-inter text-sm text-gray-600" color="black">
                          → Tax
                        </Text>
                        <div className="flex items-center gap-2 h-9 px-3 rounded-full border border-gray-200 bg-white">
                          <input
                            type="text"
                            value={rule.gst}
                            onChange={(e) =>
                              setPricingRules((prev) =>
                                prev.map((p) =>
                                  p.id === rule.id ? { ...p, gst: e.target.value } : p
                                )
                              )
                            }
                            className="w-12 outline-none font-inter text-sm bg-transparent"
                            inputMode="decimal"
                          />
                          <Text className="font-inter text-sm" color="black">
                            %
                          </Text>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setPricingRules((prev) => prev.filter((p) => p.id !== rule.id))
                          }
                          className="ml-auto p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          aria-label="Delete rule"
                        >
                          <MdDelete size={18} />
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() =>
                        setPricingRules((prev) => [
                          ...prev,
                          { id: uid(), operator: '>', price: '2000', gst: '12' },
                        ])
                      }
                      className="mt-3 px-4 py-2 border border-[#2563EB] text-[#2563EB] rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-[#EFF6FF] transition-colors w-fit"
                    >
                      <span className="font-bold text-lg leading-none">+</span>
                      <span>Add Rule</span>
                    </button>
                  </div>
                  <div className="mt-4 flex items-center gap-5">
                    <Text className="font-inter text-sm" color="black">
                      Mark as default
                    </Text>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={defaultTaxSection === 'advanced'}
                      onClick={() =>
                        setDefaultTaxSection((prev) => (prev === 'advanced' ? null : 'advanced'))
                      }
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#4D7AFF] focus:ring-offset-2 ${
                        defaultTaxSection === 'advanced' ? 'bg-[#4D7AFF]' : 'bg-[#E5E7EB]'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                          defaultTaxSection === 'advanced' ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    );
  }
);

PricingModal.displayName = 'PricingModal';

export default PricingModal;
